import { useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';

/**
 * Latency-tracker for unwind WS events. The backend stamps every emitted
 * payload with `_meta = { serverEmittedAt, seq }`. Each event we listen
 * to here gets the receive timestamp, and the first
 * `requestAnimationFrame` after the listener fires gets the render
 * timestamp — which is the cheapest accurate proxy for "the new state
 * is on the screen".
 *
 * Samples are batched and flushed every FLUSH_INTERVAL_MS via
 * `socket.emit('latency_report', { samples })`. The server computes
 * histogram percentiles and exposes them at `/unwind/metrics/latency`.
 *
 * Clock skew between client and server is unavoidable. Backend drops
 * negative `wsDeliveryMs` samples (clock ahead) and trusts only
 * `reactRenderMs` for skew-free numbers.
 */
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 200;

interface Sample {
  event: string;
  seq: number;
  serverEmittedAt: number;
  clientReceivedAt: number;
  renderedAt?: number;
}

export function recordReceived(
  buf: Sample[],
  event: string,
  payload: unknown,
): Sample | null {
  if (
    !payload ||
    typeof payload !== 'object' ||
    !('_meta' in payload)
  ) {
    return null;
  }
  const meta = (payload as { _meta: { serverEmittedAt?: number; seq?: number } })
    ._meta;
  if (
    typeof meta?.serverEmittedAt !== 'number' ||
    typeof meta?.seq !== 'number'
  ) {
    return null;
  }
  const sample: Sample = {
    event,
    seq: meta.seq,
    serverEmittedAt: meta.serverEmittedAt,
    clientReceivedAt: Date.now(),
  };
  buf.push(sample);
  // First rAF after the listener fires is "frame on which new state lands
  // in the layout pass" — close enough to perceived render latency for
  // our purposes without involving paint timing or component refs.
  requestAnimationFrame(() => {
    sample.renderedAt = Date.now();
  });
  if (buf.length > MAX_BATCH_SIZE) {
    buf.splice(0, buf.length - MAX_BATCH_SIZE);
  }
  return sample;
}

/**
 * Hook to start a periodic flush of buffered samples to the server.
 * Call once from the unwinding socket hook with the active socket and
 * a stable buffer ref.
 */
export function useLatencyTracker(
  socket: Socket | null,
  buf: { current: Sample[] },
): void {
  const flushRef = useRef<number | null>(null);

  useEffect(() => {
    if (!socket) return;
    flushRef.current = window.setInterval(() => {
      if (!socket.connected) return;
      if (buf.current.length === 0) return;
      const samples = buf.current.splice(0, buf.current.length);
      try {
        socket.emit('latency_report', { samples });
      } catch {
        // Drop samples silently — losing one batch is harmless.
      }
    }, FLUSH_INTERVAL_MS);
    return () => {
      if (flushRef.current !== null) {
        window.clearInterval(flushRef.current);
        flushRef.current = null;
      }
    };
  }, [socket, buf]);
}

export type { Sample as LatencySample };
